// eslint-disable-next-line
import React from 'react';
import { createPaginator } from '@orange-marmalade/paginate-this';
import { fromJS, Record } from 'immutable';
import { createDuck } from 'redux-duck';
import PaginateThisDuckCreator from '../middleware/paginate-this';
import { api, cleanQuery, formatError } from '../middleware/client';
import { MEMBER_TYPE_MAP } from './AgentSearch';

const BASE_URL = `${process.env.REACT_APP_REST_URI}/offices`;
const locator = ({ OfficeSearch }) => OfficeSearch.get('list');
const listId = 'offices';
const defaultQuery = {
  page: 1,
  pageSize: 16,
  sort: 'company',
  sortReverse: false
};
const sortFields = [
  {
    id: 'company',
    name: 'Name: A-Z',
    order: 'ASC'
  },
  {
    id: 'company',
    name: 'Name: Z-A',
    order: 'DESC'
  }
];

const MemberExtendedRecord = Record({
  photo: '',
  website: '',
  content: '',
  lang: '',
  firmPrefEmailAddress: ''
});

const OfficeRecord = Record({
  id: undefined,
  company: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  phone2: '',
  fax: '',
  type: '',
  licenseNumber: '',
  brokerAccountNumber: '',
  mlsYN: '',
  active: '',
  joinDate: null,
  termDate: null,
  changeDate: null,
  website: '',
  emailAddress: '',
  primarySpecialty: '',
  extended: new MemberExtendedRecord(),
  numAgents: null,
  numListings: null,
  photo: '',
  officeType: 'brokerage'
});

const paginateReducer = createPaginator({
  listId,
  fetch,
  locator,
  cache: true,
  initialSettings: {
    // initialized is true to prevent extra actions being fired
    // since we actually initialize the list using query parameters
    initialized: true,
    ...defaultQuery
  }
});

const duck = createDuck('OfficeSearch', 'nabor');
const LOAD_TYPE = duck.defineType('LOAD');
const LOAD_SUCCESS_TYPE = duck.defineType('LOAD_SUCCESS');
const LOAD_FAIL_TYPE = duck.defineType('LOAD_FAIL');
const ABORTED_REQUEST_TYPE = duck.defineType('ABORTED_REQUEST');
const ABORTED_REQUEST = duck.createAction(ABORTED_REQUEST_TYPE);

// custom abort support
var lastRequest;

const initialState = fromJS({
  list: paginateReducer(),
  detail: {
    loaded: false,
    loading: false,
    record: new OfficeRecord()
  }
});

function reducer(state = initialState, action) {
  if (action.type === LOAD_TYPE) {
    state = state.mergeIn(['detail'], {
      loaded: action.id,
      loading: true,
      error: null
    });
  } else if (action.type === LOAD_SUCCESS_TYPE) {
    state = state.mergeIn(['detail'], {
      loaded: action.id,
      loading: false,
      record: fromJS(action.payload || {}, function (key, value, path) {
        if (key === '') {
          return new OfficeRecord(value);
        } else {
          return value;
        }
      })
    });
  } else if (action.type === LOAD_FAIL_TYPE) {
    state = state.mergeIn(['detail'], {
      loading: false,
      error: formatError(action.error)
    });
  } else if (action.type === ABORTED_REQUEST_TYPE) {
    state = state.setIn(['list', 'isLoading'], false);
  } else {
    state = state.setIn(['list'], paginateReducer.call(this, state.get('list'), action));
  }
  return state;
}

function abortRequest() {
  if (lastRequest && lastRequest.abort) {
    lastRequest.abort();
  }

  return ABORTED_REQUEST(lastRequest);
}

function fetch(params) {
  const { id, query } = params;

  if (id) {
    let url = `${BASE_URL}/${encodeURIComponent(id)}`;
    return {
      types: [LOAD_TYPE, LOAD_SUCCESS_TYPE, LOAD_FAIL_TYPE],
      promise: client =>
        client.get(url).then(function (response) {
          return response;
        }),
      id
    };
  } else {
    lastRequest = api.get(BASE_URL, {
      query: cleanQuery({
        ...query,
        page: undefined,
        pageSize: undefined,
        offset: query.pageSize * (query.page - 1),
        limit: query.pageSize
      })
    });

    return lastRequest
      .then(function (response) {
        return {
          data: {
            results: response.list,
            total_count: response.total
          }
        };
      })
      .catch(error => {
        throw formatError(error);
      });
  }
}

const paginateThis = new PaginateThisDuckCreator({
  listId,
  locator,
  defaultQuery,
  abortRequest,
  // Lists filters that will automatically get parsed from the query string
  availableFilters: {
    officeType: undefined,
    name: undefined
  }
});

export { paginateThis, fetch, sortFields, OfficeRecord, MEMBER_TYPE_MAP };
export default reducer;



// WEBPACK FOOTER //
// ./src/ducks/OfficeSearch.js